import { Card, Table, Tag, Empty, Button, Space, Popconfirm, message } from 'antd';
import { CheckCircleOutlined, BellOutlined, WarningOutlined, CheckCircleFilled, InfoCircleFilled, EyeOutlined, EyeInvisibleOutlined } from '@ant-design/icons';
import { useState, useEffect } from 'react';
import { api } from '../../utils/request';
import { useAuthStore } from '../../stores/auth';
import { useTranslation } from '../../language';

interface Notification {
  id: string;
  title: string;
  content: string;
  type: string;
  isRead: number;
  createTime: string;
}

const TYPE_CONFIG: Record<string, { color: string; icon: React.ReactNode }> = {
  info: { color: 'blue', icon: <InfoCircleFilled /> },
  success: { color: 'green', icon: <CheckCircleFilled /> },
  warning: { color: 'orange', icon: <WarningOutlined /> },
  error: { color: 'red', icon: <WarningOutlined /> },
  system: { color: 'purple', icon: <BellOutlined /> },
  ticket: { color: 'cyan', icon: <BellOutlined /> },
};

export default function Notifications() {
  const { t } = useTranslation();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(false);
  const { setUnreadCount, unreadCount } = useAuthStore();

  const TYPE_LABELS: Record<string, string> = {
    info: t('menu.notificationsNamespace.typeInfo'),
    success: t('menu.notificationsNamespace.typeSuccess'),
    warning: t('menu.notificationsNamespace.typeWarning'),
    error: t('menu.notificationsNamespace.typeError'),
    system: t('menu.notificationsNamespace.typeSystem'),
    ticket: t('menu.notificationsNamespace.typeTicket'),
  };

  useEffect(() => {
    const controller = new AbortController();
    api.get<Notification[]>('/notifications', { signal: controller.signal })
      .then(data => setNotifications(Array.isArray(data) ? data : []))
      .catch((err: any) => {
        if (err.name !== 'CanceledError' && err.name !== 'AbortError') {
          console.error('Failed to load notifications:', err);
        }
      })
      .finally(() => setLoading(false));

    api.get<{ count: number }>('/notifications/unread-count', { signal: controller.signal })
      .then(data => setUnreadCount(data?.count ?? 0))
      .catch(() => {});
    return () => controller.abort();
  }, []);

  const handleMarkRead = async (id: string) => {
    try {
      await api.post(`/notifications/${id}/read`);
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, isRead: 1 } : n));
      setUnreadCount(Math.max(0, useAuthStore.getState().unreadCount - 1));
    } catch {
      message.error(t('menu.notificationsNamespace.operationFailed'));
    }
  };

  const handleMarkUnread = async (id: string) => {
    setNotifications(prev => prev.map(n => n.id === id ? { ...n, isRead: 0 } : n));
    setUnreadCount(useAuthStore.getState().unreadCount + 1);
  };

  const handleMarkAllRead = async () => {
    try {
      await api.post('/notifications/read-all');
      setNotifications(prev => prev.map(n => ({ ...n, isRead: 1 })));
      setUnreadCount(0);
      message.success(t('menu.notificationsNamespace.markAllReadSuccess'));
    } catch {
      message.error(t('menu.notificationsNamespace.operationFailed'));
    }
  };

  const columns = [
    {
      title: t('menu.notificationsNamespace.type'),
      dataIndex: 'type',
      key: 'type',
      width: 80,
      render: (type: string) => {
        const config = TYPE_CONFIG[type] || TYPE_CONFIG.info;
        return <Tag icon={config.icon} color={config.color}>{TYPE_LABELS[type] || type}</Tag>;
      },
    },
    {
      title: t('menu.notificationsNamespace.title'),
      dataIndex: 'title',
      key: 'title',
      render: (title: string, record: Notification) => (
        <a
          onClick={() => !record.isRead && handleMarkRead(record.id)}
          style={{ fontWeight: record.isRead ? 400 : 600 }}
          className={record.isRead ? 'text-gray-400' : 'text-gray-900'}
        >
          {record.type === 'ticket' ? t('menu.notificationsNamespace.ticketReplied') : title}
        </a>
      ),
    },
    {
      title: t('menu.notificationsNamespace.content'),
      dataIndex: 'content',
      key: 'content',
      ellipsis: true,
    },
    {
      title: t('menu.notificationsNamespace.status'),
      dataIndex: 'isRead',
      key: 'isRead',
      width: 90,
      render: (read: number) => read
        ? <Tag color="default">{t('menu.notificationsNamespace.read')}</Tag>
        : <Tag color="blue">{t('menu.notificationsNamespace.unread')}</Tag>,
    },
    {
      title: t('menu.notificationsNamespace.time'),
      dataIndex: 'createTime',
      key: 'createTime',
      width: 170,
      render: (time: string) => time ? new Date(time).toLocaleString() : '-',
    },
    {
      title: t('menu.notificationsNamespace.action'),
      key: 'action',
      width: 160,
      render: (_: any, record: Notification) => (
        <Space size={4}>
          {!record.isRead ? (
            <Button type="link" size="small" icon={<CheckCircleOutlined />} onClick={() => handleMarkRead(record.id)} style={{ padding: '0 4px' }}>
              {t('menu.notificationsNamespace.markRead')}
            </Button>
          ) : (
            <Button type="link" size="small" icon={<EyeInvisibleOutlined />} onClick={() => handleMarkUnread(record.id)} style={{ padding: '0 4px' }}>
              {t('menu.notificationsNamespace.markUnread')}
            </Button>
          )}
        </Space>
      ),
    },
  ];

  const currentUnreadCount = notifications.filter(n => !n.isRead).length;

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold mb-0">
          {t('menu.notificationsNamespace.pageTitle')}
          {(currentUnreadCount > 0 || unreadCount > 0) && (
            <span className="ml-2 text-base font-normal text-blue-500">({currentUnreadCount > 0 ? currentUnreadCount : unreadCount})</span>
          )}
        </h1>
        {(currentUnreadCount > 0 || unreadCount > 0) && (
          <Space>
            <Popconfirm
              title={t('menu.notificationsNamespace.markAllReadConfirm')}
              onConfirm={handleMarkAllRead}
              okText={t('menu.notificationsNamespace.confirm')}
              cancelText={t('menu.notificationsNamespace.cancel')}
            >
              <Button icon={<CheckCircleOutlined />}>{t('menu.notificationsNamespace.markAllRead')}</Button>
            </Popconfirm>
          </Space>
        )}
      </div>

      <Card>
        {notifications.length > 0 ? (
          <Table
            columns={columns}
            dataSource={notifications}
            rowKey="id"
            loading={loading}
            pagination={{ pageSize: 10 }}
          />
        ) : (
          <Empty description={t('menu.notificationsNamespace.noNotifications')} />
        )}
      </Card>
    </div>
  );
}
